<template>
  <div class="card white" style="display: flex; flex-direction: row; margin-bottom: 20px;">
    <div class="menu-item-image-container">
      <img :src="mediaSrc(recipe.photo)" class="menu-item-image" />
      <div v-if="recipe.rate !== 0" class="menu-item-rate">{{ recipe.rate }}</div>
    </div>

    <div style="flex:1">
      <div style="display:flex; gap:10px; align-items:center; margin-bottom:1rem;">
        <div class="form-title" style="color:#3c6610;">
          {{ recipe.dish_type_display }} : {{ recipe.name }}
        </div>

        <div class="tag"><font-awesome-icon icon="utensils" /> {{ recipe.difficulty }}</div>
        <div class="tag"><font-awesome-icon icon="clock" /> {{ recipe.preparation_time }} min</div>
        <div class="tag">{{ recipe.price }}</div>
        <div class="tag">{{ humanizeGlycemicIndex(recipe.glycemic_index) }}</div>
        <div class="tag">{{ humanizeMacroNutriments(recipe.macro_nutriments) }}</div>

        <div v-if="hasMenuItemContext" style="margin-left:auto;">
          <button class="button gray circle" @click.stop="showReplaceModal = true">
            <font-awesome-icon class="icon" :icon="['fas', 'arrows-rotate']" />
          </button>
        </div>

        <LikeBtn :value="recipe.liked" @toggle="toggleLike" />
        <SocialSharingModal :dish="recipe" />
      </div>

      <div class="menu-item-ingredients">
        <ul>
          <li v-for="(ing, index) in ingredients" :key="index">
            {{ ingredientName(ing) }} x {{ ing.quantity }} {{ ing.unit }}
          </li>
        </ul>
      </div>

      <div class="menu-item-recipe">{{ recipe.recipe }}</div>

      <div style="display:flex; justify-content:space-between; margin-top:2rem; margin-bottom:1rem;">
        <div class="menu-item-name">
          <span v-if="recipe.my_comment">Votre commentaire</span>
          <span v-else>Soyez le premier à écrire un commentaire !</span>
        </div>

        <div>
          <font-awesome-icon
            v-for="i in 5"
            :key="i"
            :icon="getReviewStarIcon(i, recipe.my_rate)"
            @mouseover="hoverStar(i)"
            @mouseleave="resetHover"
            @click="selectStar(i)"
            class="star"
          />
        </div>
      </div>

      <div v-if="reviewEditionMode" style="margin-top:10px;">
        <textarea class="form-input" v-model="myReview" rows="3" placeholder="Votre commentaire" />
        <div style="margin-top:10px; text-align:right;">
          <button v-if="recipe.my_comment" class="button gray" @click="reviewEditionMode = false">Annuler</button>
          <button class="button" @click="saveMyReview">Envoyer</button>
        </div>
      </div>

      <div v-else class="review">
        <div>{{ recipe.my_comment.comment }}</div>
        <button class="button gray" @click="editReview">Modifier</button>
      </div>
    </div>

    <ReplaceDishModal
      v-if="showReplaceModal && hasMenuItemContext"
      :item="item"
      :date="dateFromRoute"
      @close="showReplaceModal = false"
      @update="updateRecipe"
    />
  </div>
</template>

<script>
import SocialSharingModal from "@/components/SocialSharingModal.vue";
import LikeBtn from "@/components/LikeBtn.vue";
import ReplaceDishModal from "@/components/ReplaceDishModal.vue";
import { api } from "@/api";

export default {
  name: "DishCard",
  components: { SocialSharingModal, LikeBtn, ReplaceDishModal },

  props: {
    recipe: { type: Object, required: true },
    item: { type: Object, default: null },
    adjustedIngredients: { type: Array, default: null },
  },

  data() {
    return {
      hoveredStar: 0,
      myReview: "",
      reviewEditionMode: !this.recipe.my_comment,
      showReplaceModal: false,
    };
  },

  computed: {
    hasMenuItemContext() {
      return !!this.item;
    },
    ingredients() {
      const HIDDEN = ["Poivre noir, poudre", "Sel blanc alimentaire"];

      const filterHidden = (list) =>
        list.filter((ing) => {
          const name = ing?.aliment?.display_name || ing?.aliment?.name || ing?.name || "";
          return !HIDDEN.includes(name);
        });

      if (Array.isArray(this.adjustedIngredients)) {
        return filterHidden(this.adjustedIngredients);
      }

      if (Array.isArray(this.item?.ajusted_ingredients)) {
        return filterHidden(this.item.ajusted_ingredients);
      }

      if (Array.isArray(this.recipe?.dish_lines)) {
        return filterHidden(this.recipe.dish_lines);
      }

      return [];
    },
    dateFromRoute() {
      return this.$route.params.date;
    },
  },

  methods: {
    humanizeGlycemicIndex(g) {
      if (g === "low") return "Index glycémique bas";
      if (g === "medium") return "Index glycémique moyen";
      return "Index glycémique élevé";
    },
    humanizeMacroNutriments(value) {
      if (!value) return "";
      const map = { P: "Protéines", L: "Lipides", G: "Glucides" };
      return value.split("-").map(l => map[l] || l).join(" - ");
    },

    ingredientName(ingredient) {
      return ingredient?.aliment?.display_name || ingredient?.aliment?.name || ingredient?.name || "";
    },

    emitUpdatedRecipe(recipePatch) {
      if (this.hasMenuItemContext) {
        this.$emit("update", {
          ...this.item,
          dish: recipePatch,
        });
        return;
      }

      this.$emit("update", recipePatch);
    },

    getReviewStarIcon(i, rate) {
      if (this.hoveredStar >= i) return ["fas", "star"];
      return rate >= i ? ["fas", "star"] : ["far", "star"];
    },
    hoverStar(i) {
      this.hoveredStar = i;
    },
    resetHover() {
      this.hoveredStar = 0;
    },

    selectStar(i) {
      api.post(`/recipes/${this.recipe.id}/rate/`, { rating: i }).then((res) => {
        this.emitUpdatedRecipe({ ...this.recipe, my_rate: res.data.my_rate });
      });
    },

    saveMyReview() {
      api.post(`/recipes/${this.recipe.id}/comment/`, { comment: this.myReview }).then((res) => {
        this.emitUpdatedRecipe({ ...this.recipe, my_comment: res.data.my_comment });
        this.reviewEditionMode = false;
      });
    },

    editReview() {
      this.myReview = this.recipe.my_comment.comment;
      this.reviewEditionMode = true;
    },

    toggleLike() {
      api.post(`/recipes/${this.recipe.id}/toggle_like/`).then((res) => {
        this.emitUpdatedRecipe({
          ...this.recipe,
          liked: !this.recipe.liked,
          nb_likes: res.data.nb_likes,
        });
      });
    },

    mediaSrc(uri) {
      if (!uri) return null;
      if (uri.startsWith("http")) return uri;
      return `${process.env.VUE_APP_BASE_URL}${uri}`;
    },

    updateRecipe(newItem) {
      this.$emit("update", this.hasMenuItemContext ? newItem : newItem?.dish || this.recipe);
      this.showReplaceModal = false;
    },
  },
};
</script>

<style scoped>
.menu-item-image-container {
  width: 200px;
  object-fit: cover;
  margin-right: 20px;
  border-radius: 10px;
  position: relative;
}

.menu-item-image {
  width: 100%;
  height: 100%;
  border-radius: 10px;
  object-fit: cover;
}

.menu-item-rate {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 30px;
  height: 30px;
  background: gold;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.menu-item-name {
  font-size: 20px;
  font-weight: bold;
  color: #3c6610;
}

.menu-item-ingredients {
  font-size: 14px;
}

.menu-item-recipe {
  font-size: 14px;
}

.tag {
  background: #b5d088;
  color: white;
  padding: 4px 12px;
  border-radius: 5px;
  display: inline-block;
}

.star {
  font-size: 20px;
  cursor: pointer;
  transition: color 0.3s ease;
  color: gold;
}

.star:hover {
  color: gold;
}

.review {
  align-items: center;
  justify-content: space-between;
  display: flex;
  border-radius: 5px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  padding: 16px;
  max-width: 500px;
}
</style>
